import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { SettingsProvider } from './context/SettingsContext';
import { LocationProvider } from './context/LocationContext';
import { Layout } from './components/layout';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import BrowseCars from './pages/Public/BrowseCars';
import CustomerLogin from './pages/Public/CustomerLogin';
import TrackBooking from './pages/Public/TrackBooking';
import CarList from './pages/Cars/CarList';
import AddCar from './pages/Cars/AddCar';
import CarDetails from './pages/Cars/CarDetails';
import EditCar from './pages/Cars/EditCar';
import DocumentUpload from './pages/Cars/DocumentUpload';
import BookingList from './pages/Bookings/BookingList';
import CreateBooking from './pages/Bookings/CreateBooking';
import JourneyList from './pages/Journeys/JourneyList';
import JourneyDetails from './pages/Journeys/JourneyDetails';
import Profile from './pages/Profile';
import Maintenance from './pages/Maintenance';
import Drivers from './pages/Drivers';

import Settings from './pages/Settings';
import Reports from './pages/Reports/Reports';
import StaffList from './pages/Staff/StaffList';
import StaffForm from './pages/Staff/StaffForm';
import StaffDetails from './pages/Staff/StaffDetails';
import ActiveTrip from './pages/Driver/ActiveTrip';
import TrackDriver from './pages/Public/TrackDriver';
import Support from './pages/Public/Support';
import TermsAndConditions from './pages/Public/TermsAndConditions';
import PrivacyPolicy from './pages/Public/PrivacyPolicy';
import RefundPolicy from './pages/Public/RefundPolicy';
import AboutUs from './pages/Public/AboutUs';
import Home from './pages/Public/Home';
import Pricing from './pages/Public/Pricing';
import Grievance from './pages/Public/Grievance';
import VehicleShare from './pages/Public/VehicleShare';
import OfflineBanner from './components/OfflineBanner';
import TravelsProfile from './pages/Public/TravelsProfile';
import OwnersList from './pages/Admin/OwnersList';
import AdminUsers from './pages/Admin/AdminUsers';
import AllUsers from './pages/Admin/AllUsers';
import MyDocuments from './pages/MyDocuments';
import Packages from './pages/Packages';
import Notifications from './pages/Notifications';
import OneWayTripList from './pages/OneWayTrips/OneWayTripList';
import CreateOneWayTrip from './pages/OneWayTrips/CreateOneWayTrip';
import OwnerSetup from './pages/Setup/OwnerSetup';
import Subscription from './pages/Subscription';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  if (!isAuthenticated) return <Navigate to="/home" />;

  // Redirect owner to setup if not completed
  if (user?.role === 'owner' && !user?.setupComplete) {
    return <Navigate to="/setup" />;
  }

  return children;
};

// Role-based Route Guard (+ optional owner business-type guard)
const RoleRoute = ({ children, allowedRoles, business }) => {
  const { user } = useAuth();

  if (!user || !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Block rental/sale-specific routes when they don't match the owner's business
  // type (admins bypass). Prevents reaching a hidden section by typing the URL.
  if (business && user.role !== 'admin' && !business.includes(user.businessType || 'both')) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Public Routes - No Login Required */}
      <Route path="/" element={<Home />} />
      <Route path="/home" element={<Home />} />
      <Route path="/browse" element={<BrowseCars />} />
      <Route path="/track-booking" element={<TrackBooking />} />
      <Route path="/track/:trackingCode" element={<TrackDriver />} />
      <Route path="/customer-login" element={<CustomerLogin />} />
      <Route path="/support" element={<Support />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/grievance" element={<Grievance />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/travels/:ownerId" element={<TravelsProfile />} />
      <Route path="/vehicle/:carId" element={<VehicleShare />} />

      {/* Auth Routes */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />}
      />
      <Route
        path="/register"
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <Register />}
      />
      <Route
        path="/forgot-password"
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <ForgotPassword />}
      />

      {/* Owner Setup (first-time onboarding) */}
      <Route
        path="/setup"
        element={isAuthenticated ? <OwnerSetup /> : <Navigate to="/login" />}
      />

      {/* Protected Routes with Layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="dashboard" element={<Dashboard />} />

        {/* Car Routes - owner, admin, manager, dispatcher */}
        <Route path="cars" element={<RoleRoute allowedRoles={['owner', 'admin', 'manager', 'dispatcher']}><CarList /></RoleRoute>} />
        <Route path="cars/add" element={<RoleRoute allowedRoles={['owner', 'manager']}><AddCar /></RoleRoute>} />
        <Route path="cars/:id" element={<RoleRoute allowedRoles={['owner', 'admin', 'manager', 'dispatcher']}><CarDetails /></RoleRoute>} />
        <Route path="cars/edit/:id" element={<RoleRoute allowedRoles={['owner', 'manager']}><EditCar /></RoleRoute>} />
        <Route path="cars/:id/documents/upload" element={<RoleRoute allowedRoles={['owner', 'manager']}><DocumentUpload /></RoleRoute>} />

        {/* Booking Routes - owner, admin, manager, accountant, dispatcher */}
        <Route path="bookings" element={<RoleRoute allowedRoles={['owner', 'admin', 'manager', 'accountant', 'dispatcher']} business={['rental', 'both']}><BookingList /></RoleRoute>} />
        <Route path="bookings/create" element={<RoleRoute allowedRoles={['owner', 'admin', 'manager', 'dispatcher']} business={['rental', 'both']}><CreateBooking /></RoleRoute>} />

        {/* Journey Routes - owner, admin, manager, dispatcher, driver */}
        <Route path="journeys" element={<RoleRoute allowedRoles={['owner', 'admin', 'manager', 'dispatcher', 'driver']} business={['rental', 'both']}><JourneyList /></RoleRoute>} />
        <Route path="journeys/:id" element={<RoleRoute allowedRoles={['owner', 'admin', 'manager', 'dispatcher', 'driver']} business={['rental', 'both']}><JourneyDetails /></RoleRoute>} />

        {/* Driver Active Trip Routes - driver */}
        <Route path="driver/active-trip/:journeyId" element={<RoleRoute allowedRoles={['owner', 'admin', 'manager', 'dispatcher', 'driver']} business={['rental', 'both']}><ActiveTrip /></RoleRoute>} />
        <Route path="driver/active-trip/booking/:bookingId" element={<RoleRoute allowedRoles={['owner', 'admin', 'manager', 'dispatcher', 'driver']} business={['rental', 'both']}><ActiveTrip /></RoleRoute>} />

        {/* Staff/Team Routes - owner, admin */}
        <Route path="staff" element={<RoleRoute allowedRoles={['owner', 'admin']}><StaffList /></RoleRoute>} />
        <Route path="staff/new" element={<RoleRoute allowedRoles={['owner', 'admin']}><StaffForm /></RoleRoute>} />
        <Route path="staff/:id" element={<RoleRoute allowedRoles={['owner', 'admin']}><StaffDetails /></RoleRoute>} />
        <Route path="staff/:id/edit" element={<RoleRoute allowedRoles={['owner', 'admin']}><StaffForm /></RoleRoute>} />

        {/* Admin Routes - admin only */}
        <Route path="admin/owners" element={<RoleRoute allowedRoles={['admin']}><OwnersList /></RoleRoute>} />
        <Route path="admin/users" element={<RoleRoute allowedRoles={['admin']}><AllUsers /></RoleRoute>} />
        <Route path="admin/admins" element={<RoleRoute allowedRoles={['admin']}><AdminUsers /></RoleRoute>} />

        {/* One-Way Trip Routes - owner, admin, manager, dispatcher, driver */}
        <Route path="oneway-trips" element={<RoleRoute allowedRoles={['owner', 'admin', 'manager', 'dispatcher', 'driver']} business={['rental', 'both']}><OneWayTripList /></RoleRoute>} />
        <Route path="oneway-trips/create" element={<RoleRoute allowedRoles={['owner', 'admin', 'manager', 'dispatcher', 'driver']} business={['rental', 'both']}><CreateOneWayTrip /></RoleRoute>} />
        <Route path="oneway-trips/edit/:id" element={<RoleRoute allowedRoles={['owner', 'admin', 'manager', 'dispatcher', 'driver']} business={['rental', 'both']}><CreateOneWayTrip /></RoleRoute>} />

        {/* Other Routes */}
        <Route path="drivers" element={<RoleRoute allowedRoles={['owner', 'admin', 'manager', 'dispatcher']} business={['rental', 'both']}><Drivers /></RoleRoute>} />
        <Route path="reports" element={<RoleRoute allowedRoles={['owner', 'admin', 'manager', 'accountant']}><Reports /></RoleRoute>} />
        <Route path="settings" element={<RoleRoute allowedRoles={['owner', 'admin']}><Settings /></RoleRoute>} />
        <Route path="subscription" element={<RoleRoute allowedRoles={['owner']}><Subscription /></RoleRoute>} />
        <Route path="maintenance" element={<RoleRoute allowedRoles={['owner', 'admin', 'manager']}><Maintenance /></RoleRoute>} />
        <Route path="my-documents" element={<RoleRoute allowedRoles={['owner']}><MyDocuments /></RoleRoute>} />
        <Route path="packages" element={<RoleRoute allowedRoles={['owner', 'admin', 'manager']}><Packages /></RoleRoute>} />

        {/* Profile & Notifications - all roles */}
        <Route path="profile" element={<Profile />} />
        <Route path="notifications" element={<Notifications />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <SettingsProvider>
          <LocationProvider>
            <NotificationProvider>
              <OfflineBanner />
              <AppRoutes />
            </NotificationProvider>
          </LocationProvider>
        </SettingsProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
